import { getRequest, postRequest } from "../utils/fetch";
import { deserialiseDate } from "../utils/date";

const basePath = "/api/v1/StorageService.svc/json";

export type StoreCode = "eXpress" | "Facebook" | "GooglePhotos" | "Instagram";
export interface IPhotoStore {
	readonly Name: string;
	ConnectedUserName: string;
	IsConnected: boolean;
	HasAlbumAccess: boolean;
	readonly ShowAlbum: boolean;
	readonly ShowTokenExpiry: boolean;
	readonly StoreType: StoreType; // numeric value
	readonly Code: string; // for string value of storeType
	readonly LocatePhotosUrl?: string;
	readonly ImportPhotosUrl?: string;
	readonly UseExternalPhotoPicker: boolean;
}

export enum StoreType {
	eXpress = 0,
	GooglePhotos = 2,
	Facebook = 3,
	SwissCom = 5,
	Local = 10,
	QrCode = 11,
}

export interface ExternalPhotoPickerResult {
	readonly Id: string;
	readonly MediaItemsSet: boolean;
	readonly PickerUri: string;
	readonly PollingIntervalInSec: number;
	readonly ExpiresInSec: number;
}

export interface ApiPhotos {
	readonly Photos: ApiPhoto[];
	readonly StoreType: StoreType;
	readonly AccessToken?: string;
}

interface ApiPhoto {
	readonly Code: string;
	readonly Name: string;
	readonly OriginalFilename: string;
	readonly ThumbUrl: string;
	readonly MediumUrl: string;
	readonly HiresUrl: string;
	readonly DownloadUrl: string;
	readonly Width: number;
	readonly Height: number;
	readonly DateTaken: string;
}

interface ApiSmartphotoPhoto extends ApiPhoto {
	readonly Id: string;
	readonly AccountId: string;
	readonly FolderId: string;
	readonly Description: string;
	readonly RelativeMediumUrl: string;
	readonly Rotate90: number;
	readonly FileSize: number;
	readonly DateUploaded: string;
	readonly DateModified: string;
	readonly Hash: string;
}

interface Photo {
	readonly fileId: string;
	readonly code: string;
	readonly name: string;
	readonly description: string;
	readonly originalFilename: string;
	readonly thumbUrl: string;
	readonly mediumUrl: string;
	readonly width: number;
	readonly height: number;
	readonly dateTaken: Date;
	readonly dateUploaded: Date;
	readonly dateModified: Date;
	readonly fileSize: number;
}

export interface SmartphotoPhoto extends Photo {
	readonly rotate90: number;
	readonly folderId: string;
	readonly hash: string;
	readonly relativeMediumUrl: string;
	readonly mediumUrlOriginal: string;
}

export interface PartnerPhoto extends Photo {
	readonly hiresUrl: string;
	readonly downloadUrl: string;
	readonly storeType: StoreType;
	readonly accessToken?: string;
}

export function getPhotoStoreAsync(storeName: string, maxNumberOfRetries = 0, retry = 0): Promise<IPhotoStore> {
	const pointOfSaleId = window.context.pointOfSaleId;
	const language = window.context.language;

	const args = {
		storeName,
		pointOfSaleId,
		language,
		maxNumberOfRetries,
		retry,
	};

	const data = new URLSearchParams();
	Object.keys(args).forEach((key) => data.append(key, args[key]));

	return getRequest(basePath + "/GetPhotoStore?" + data).then((res: { GetPhotoStoreResult: IPhotoStore }) => {
		const store = res.GetPhotoStoreResult;
		return {
			...store,
			Code: StoreType[store.StoreType],
		} as IPhotoStore;
	});
}

export function getPhotoStoresAsync(
	pointOfSaleId: string,
	language: string,
	skipIsConnectedCheck = false,
	skipPhotoDownload = false,
	skipSignleSignOn = false,
	maxNumberOfRetries = 0,
	retry = 0,
): Promise<IPhotoStore[]> {
	const args = {
		pointOfSaleId,
		language,
		maxNumberOfRetries,
		retry,
		skipIsConnectedCheck,
		skipPhotoDownload,
		skipSignleSignOn,
	};

	const data = new URLSearchParams();
	Object.keys(args).forEach((key) => data.append(key, args[key]));

	return getRequest(basePath + "/GetPhotoStores?" + data).then((res: { GetPhotoStoresResult: IPhotoStore[] }) =>
		res.GetPhotoStoresResult.map((store) => {
			return {
				...store,
				Code: StoreType[store.StoreType],
			} as IPhotoStore;
		}),
	);
}

export function deleteMediaStorePartnerAccountMemberAsync(
	storeCode: number,
	accountId: string,
	pointOfSaleId: string,
): Promise<boolean> {
	const args = {
		mediaStorePartnerType: storeCode,
		accountId,
		pointOfSaleId,
	};

	const data = new URLSearchParams();
	Object.keys(args).forEach((key) => data.append(key, args[key]));

	return getRequest(basePath + "/DeleteMediaStorePartnerAccountMember?" + data).then(
		(res: { DeleteMediaStorePartnerAccountMemberResult: boolean }) => res.DeleteMediaStorePartnerAccountMemberResult,
	);
}

export function getPhotosAsync(
	pointOfSaleId: string,
	accountId: string,
	folderId: string,
	ids: string[],
	isSelectAllActive = false,
	useDeselected = false,
): Promise<SmartphotoPhoto[]> {
	return postRequest(
		basePath + "/getExpressPhotos",
		{ accountId, pointOfSaleId, folderId, ids, isSelectAllActive, useDeselected },
		{ headers: { "Content-type": "application/json; charset=utf-8" } },
	)
		.then(
			(res: { GetExpressPhotosResult: ApiSmartphotoPhoto[] }) =>
				res && res.GetExpressPhotosResult.map((photo) => convertSmartphotoPhoto(photo)),
		)
		.catch((_) => []);
}

export function getPartnerPhotosAsync(
	pointOfSaleId: string,
	accountId: string,
	store: StoreType,
	folderCode: string,
	selectedIds: string[],
	isSelectAllActive = false,
	useDeselected = false,
): Promise<PartnerPhoto[]> {
	const args = {
		pointOfSaleId,
		accountId,
		selectedIds,
		mediaStorePartnerType: store,
		folderCode,
		isSelectAllActive,
		useDeselected,
	};
	return postRequest(basePath + "/GetMediaStorePartnerPhotos", args, {
		headers: { "Content-type": "application/json; charset=utf-8" },
	}).then((res: { GetMediaStorePartnerPhotosResult: ApiPhotos }) =>
		res.GetMediaStorePartnerPhotosResult.Photos.map((photo) =>
			convertPartnerPhoto(
				photo,
				res.GetMediaStorePartnerPhotosResult.StoreType,
				res.GetMediaStorePartnerPhotosResult.AccessToken,
			),
		),
	);
}

//#region externalPhotoPicker
export function createExternalPhotoPicker(
	storeTypeId: number,
	accountId: string,
	pointOfSaleId: string,
	maxItemCount = 0,
): Promise<ExternalPhotoPickerResult> {
	const args = {
		mediaStorePartnerType: storeTypeId,
		accountId,
		pointOfSaleId,
		maxItemCount,
	};

	const data = new URLSearchParams();
	Object.keys(args).forEach((key) => data.append(key, args[key]));

	return getRequest(basePath + "/CreateExternalPhotoPicker?" + data).then(
		(res: { CreateExternalPhotoPickerResult: ExternalPhotoPickerResult }) => res.CreateExternalPhotoPickerResult,
	);
}

export function getExternalPhotoPicker(
	storeTypeId: number,
	accountId: string,
	pointOfSaleId: string,
	sessionId: string,
): Promise<ExternalPhotoPickerResult> {
	const args = {
		mediaStorePartnerType: storeTypeId,
		accountId,
		pointOfSaleId,
		sessionId,
	};

	const data = new URLSearchParams();
	Object.keys(args).forEach((key) => data.append(key, args[key]));

	return getRequest(basePath + "/GetExternalPhotoPicker?" + data).then(
		(res: { GetExternalPhotoPickerResult: ExternalPhotoPickerResult }) => res.GetExternalPhotoPickerResult,
	);
}

export function getExternalPhotoPickerPhotos(
	storeTypeId: number,
	accountId: string,
	pointOfSaleId: string,
	sessionId: string,
): Promise<PartnerPhoto[]> {
	const args = {
		mediaStorePartnerType: storeTypeId,
		accountId,
		pointOfSaleId,
		sessionId,
	};

	const data = new URLSearchParams();
	Object.keys(args).forEach((key) => data.append(key, args[key]));

	return getRequest(basePath + "/GetExternalPhotoPickerPhotos?" + data).then(
		(res: { GetExternalPhotoPickerPhotosResult: ApiPhotos }) =>
			res.GetExternalPhotoPickerPhotosResult.Photos.map((photo) =>
				convertPartnerPhoto(
					photo,
					res.GetExternalPhotoPickerPhotosResult.StoreType,
					res.GetExternalPhotoPickerPhotosResult.AccessToken,
				),
			),
	);
}

export function deleteExternalPhotoPicker(
	storeTypeId: number,
	accountId: string,
	pointOfSaleId: string,
	sessionId: string,
): Promise<boolean> {
	const args = {
		mediaStorePartnerType: storeTypeId,
		accountId,
		pointOfSaleId,
		sessionId,
	};

	const data = new URLSearchParams();
	Object.keys(args).forEach((key) => data.append(key, args[key]));

	return postRequest(basePath + "/DeleteExternalPhotoPicker", args, {
		headers: { "Content-type": "application/json; charset=utf-8" },
	}).then((res: { DeleteExternalPhotoPickerResult: boolean }) => res.DeleteExternalPhotoPickerResult);
}
//#endregion

export function copyPhotosToFolder(
	accountId: string,
	pointOfSaleId: string,
	photoIds: string[],
	folderId: string,
): Promise<SmartphotoPhoto[]> {
	return postRequest(
		basePath + "/CopyPhotosToFolder",
		{ accountId, pointOfSaleId, photoIds, folderId },
		{ headers: { "Content-type": "application/json; charset=utf-8" } },
	).then((res: { CopyPhotosToFolderResult: ApiSmartphotoPhoto[] }) =>
		res.CopyPhotosToFolderResult.map((photo) => convertSmartphotoPhoto(photo)),
	);
}

function convertSmartphotoPhoto(photo: ApiSmartphotoPhoto): SmartphotoPhoto {
	return {
		fileId: photo.Id,
		code: photo.Id,
		name: photo.Name,
		description: photo.Description,
		originalFilename: photo.OriginalFilename,
		thumbUrl: photo.ThumbUrl,
		mediumUrl: photo.MediumUrl,
		width: photo.Width,
		height: photo.Height,
		rotate90: photo.Rotate90,
		fileSize: photo.FileSize,
		dateTaken: deserialiseDate(photo.DateTaken),
		dateUploaded: deserialiseDate(photo.DateUploaded),
		dateModified: deserialiseDate(photo.DateModified),
		folderId: photo.FolderId,
		hash: photo.Hash,
		relativeMediumUrl: photo.RelativeMediumUrl,
		mediumUrlOriginal: photo.MediumUrl,
	};
}

function convertPartnerPhoto(photo: ApiPhoto, store: StoreType, accessToken?: string): PartnerPhoto {
	return {
		fileId: photo.Code,
		code: photo.Code,
		name: photo.Name,
		description: "",
		originalFilename: photo.OriginalFilename,
		thumbUrl: photo.ThumbUrl,
		mediumUrl: photo.MediumUrl,
		width: photo.Width,
		height: photo.Height,
		dateTaken: deserialiseDate(photo.DateTaken),
		dateUploaded: deserialiseDate(photo.DateTaken),
		dateModified: deserialiseDate(photo.DateTaken),
		hiresUrl: photo.HiresUrl,
		downloadUrl: photo.DownloadUrl,
		storeType: store,
		accessToken: accessToken,
		fileSize: 0,
	};
}
